import type { Dispatch } from '@atlaskit/editor-common/event-dispatcher';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import type { PMPluginFactoryParams } from '@atlaskit/editor-common/types/plugin-factory';
import { areNodesEqualIgnoreAttrs } from '@atlaskit/editor-common/utils/document';
import type { EditorState } from '@atlaskit/editor-prosemirror/state';
import type { DecorationSet, EditorView } from '@atlaskit/editor-prosemirror/view';

import type { AIPlugin } from '../../aiPluginType';
import type { EditorPluginAIProvider } from '../../types/types';
import type { EditorPluginAIConfigItem } from '../../ui/prebuilt/config-items/config-items';
import { createNudgeDecorationSet } from './decorations';
import { IdleDetector } from './idle-detector';
import { onIdle } from './on-idle';
import {
	createPluginState,
	createHidePromptCommand,
	initialState,
	pluginKey,
} from './plugin-factory';
import type { StandDownManager } from './stand-down-manager';

/**
 * Create the ai-quick-prompt ProseMirror plugin.
 *
 * @param options - The options
 * @param options.api - The editor plugin API
 * @param options.dispatch - The plugin dispatch function from pluginFactory
 * @param options.getIntl - Function to get intl for i18n
 * @param options.product - The product identifier for API headers
 * @returns A ProseMirror Plugin instance
 */
export function createAiQuickPromptPlugin({
	api,
	configItem,
	dispatch,
	getIntl,
	product,
	standDownManager,
}: {
	api: ExtractInjectionAPI<AIPlugin> | undefined;
	configItem: EditorPluginAIConfigItem;
	dispatch: Dispatch;
	getIntl: PMPluginFactoryParams['getIntl'];
	product: EditorPluginAIProvider['product'];
	standDownManager: StandDownManager;
}): SafePlugin {
	let isCollabDocUpdate = false;

	return new SafePlugin({
		key: pluginKey,
		state: createPluginState(dispatch, initialState),

		appendTransaction: (transactions) => {
			for (const tr of transactions) {
				const isCollab = Boolean(tr.getMeta('isRemote'));
				if (isCollab && !areNodesEqualIgnoreAttrs(tr.before, tr.doc)) {
					isCollabDocUpdate = true;
				}
			}
			return null;
		},

		view(view: EditorView) {
			const idleDetector = new IdleDetector({
				onIdle: ({ signal }) =>
					onIdle({ api, configItem, getIntl, product, signal, standDownManager, view }),
			});

			// Stop the idle detector to prevent the prompt from showing after the document is updated by AI
			const unsubscribeAIState = api?.aiExperience?.sharedState.onChange(
				({ nextSharedState, prevSharedState }) => {
					const isNotRunning =
						prevSharedState?.aiDecorationExperiencePluginState?.isRunning &&
						!nextSharedState?.aiDecorationExperiencePluginState?.isRunning;
					const isSessionEnded =
						prevSharedState?.aiDecorationExperiencePluginState?.nextSessionId &&
						!nextSharedState?.aiDecorationExperiencePluginState?.nextSessionId;
					if (isNotRunning || isSessionEnded) {
						idleDetector.stop();
					}
				},
			);

			return {
				update(view: EditorView, prevState: EditorState) {
					// When the selection changes (without a document change), stop the idle
					// detector so the prompt does not appear after the user moves the cursor.
					// Prompt will be hidden by pluginFactory's onSelectionChanged callback.
					if (!prevState.selection.eq(view.state.selection)) {
						idleDetector.stop();
					}

					// When document changes, hide the prompt and reset idle timer
					// - The plugin factory's onDocChanged callback will be called whenever there is a step,
					//   - which is not always a document change.
					// - When `Create with Rovo`, there is a final replaceDocument operation
					//   - which is making `.eq` return false, so we have to use `areNodesEqualIgnoreAttrs`
					if (!areNodesEqualIgnoreAttrs(prevState.doc, view.state.doc)) {
						if (isCollabDocUpdate) {
							isCollabDocUpdate = false;
						} else {
							const pluginState = pluginKey.getState(view.state);
							if (pluginState?.isPromptVisible) {
								// Hide the prompt after a short delay to allow the editor to complete the current state update.
								// See https://hello.jira.atlassian.cloud/browse/EDITOR-3849
								setTimeout(() => {
									createHidePromptCommand()(view.state, view.dispatch);
								}, 0);
							}
							idleDetector.reset();
						}
					}
				},
				destroy() {
					unsubscribeAIState?.();
					idleDetector.destroy();
				},
			};
		},

		props: {
			decorations(state: EditorState): DecorationSet | undefined {
				const pluginState = pluginKey.getState(state);
				if (pluginState?.isPromptVisible && pluginState.eligibleRange) {
					return createNudgeDecorationSet({
						state,
						range: pluginState.eligibleRange,
						getIntl,
					});
				}
				return undefined;
			},
		},
	});
}
